import * as React from "react";
import { DndContext, PointerSensor, useDraggable, useDroppable, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { useMutation, useQuery } from "convex/react";
import type { ColumnDef } from "@tanstack/react-table";
import {
  CalendarClock,
  Check,
  CheckCircle2,
  CircleDot,
  ClipboardList,
  Columns3,
  FileUp,
  GripVertical,
  Hash,
  ListChecks,
  MapPin,
  MessageSquarePlus,
  Phone,
  Tags,
  UserPlus,
} from "lucide-react";
import { toast } from "sonner";

import { api } from "$convex/convexApi";
import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { DataTableSkeleton } from "@/components/data-table/data-table-skeleton";
import { InternalEntityDataTable } from "@/components/internal/shared/InternalEntityDataTable";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { useAuthIdentity } from "@/contexts/UserProfileContext";
import { BADGE_TONES } from "@/lib/badge-tones";
import { cn } from "@/lib/utils";
import {
  dateInputStringToLocalStartOfDayMs,
  formatDate,
  formatRelativeTime,
  timestampToLocalDateInputString,
} from "@/lib/format";
import { isAdmin } from "@/lib/roles";

type LeadStatus =
  | "new"
  | "assigned"
  | "contacted"
  | "qualified"
  | "sampling"
  | "converted"
  | "disqualified";

type CrmWorkspaceView = "table" | "pipeline" | "followups";
type FollowUpBucket = "overdue" | "thisWeek" | "later" | "noFollowUp";

type LeadApproach = "unknown" | "call" | "instagram" | "email" | "visit";
type LeadPriority = "low" | "medium" | "high";
type LeadFit = "unknown" | "specialty" | "blend_dark_flavored" | "multi_location" | "poor_fit";
type ActivityType = "call" | "email" | "social" | "note" | "sample" | "status_change" | "account_created";
type ActivityOutcome =
  | "connected"
  | "left_voicemail"
  | "no_answer"
  | "interested"
  | "not_interested"
  | "needs_follow_up"
  | "sample_requested"
  | "account_created";

interface CrmLead {
  _id: string;
  companyName: string;
  contactName?: string;
  email?: string;
  phone?: string;
  website?: string;
  instagram?: string;
  addressLine1?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  shippingDistanceDays?: number;
  source?: string;
  status: LeadStatus;
  approach: LeadApproach;
  priority: LeadPriority;
  fit: LeadFit;
  tags?: string[];
  notes?: string;
  assignedToUserProfileId?: string;
  assignedToName?: string | null;
  lastContactedAt?: number;
  nextFollowUpAt?: number;
  createdAt: number;
  updatedAt: number;
  updatedByName?: string | null;
}

interface CrmActivity {
  _id: string;
  type: ActivityType;
  outcome?: ActivityOutcome;
  note: string;
  nextFollowUpAt?: number;
  statusAfter?: LeadStatus;
  createdByName?: string | null;
  createdAt: number;
}

interface SalesAssignee {
  _id: string;
  name: string | null;
  email: string | null;
  role: string | null;
}

interface LeadDraft {
  companyName: string;
  contactName: string;
  email: string;
  phone: string;
  website: string;
  instagram: string;
  addressLine1: string;
  city: string;
  state: string;
  postalCode: string;
  shippingDistanceDays: string;
  source: string;
  status: LeadStatus;
  approach: LeadApproach;
  priority: LeadPriority;
  fit: LeadFit;
  tags: string;
  notes: string;
  assignedToUserProfileId: string;
  nextFollowUpAt: string;
}

const UNASSIGNED_VALUE = "__unassigned__";
const NO_STATUS_CHANGE_VALUE = "__no_status_change__";
const CRM_VIEW_STORAGE_KEY = "anthem.crm.workspaceView";

export function getCrmWorkspaceAccess({
  userRole,
}: {
  userRole?: string | null;
}) {
  const adminUser = isAdmin(userRole);
  const salesUser = userRole?.toLowerCase() === "sales";

  return {
    canUseCrm: adminUser || salesUser,
    canAssign: adminUser,
    showsAllLeads: adminUser,
  };
}

const LEAD_TAG_OPTIONS = ["specialty", "one-day", "multi-location", "KC", "DFW", "Houston", "poor fit", "warm lead"] as const;

const LEAD_TAG_TONES = [
  "border-sky-300 bg-sky-100 text-sky-900 dark:border-sky-800/60 dark:bg-sky-950/40 dark:text-sky-200",
  "border-violet-300 bg-violet-100 text-violet-900 dark:border-violet-800/60 dark:bg-violet-950/40 dark:text-violet-200",
  "border-orange-300 bg-orange-100 text-orange-900 dark:border-orange-800/60 dark:bg-orange-950/40 dark:text-orange-200",
  "border-cyan-300 bg-cyan-100 text-cyan-900 dark:border-cyan-800/60 dark:bg-cyan-950/40 dark:text-cyan-200",
] as const;

function pickLeadTagTone(value: unknown): string {
  const normalized = typeof value === "string" ? value.trim().toLowerCase() : "";
  if (!normalized) return LEAD_TAG_TONES[0];
  let hash = 0;
  for (const char of normalized) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  return LEAD_TAG_TONES[hash % LEAD_TAG_TONES.length]!;
}
const ALL_VALUE = "__all__";

const STATUS_OPTIONS: Array<{ value: LeadStatus; label: string }> = [
  { value: "new", label: "New" },
  { value: "assigned", label: "Assigned" },
  { value: "contacted", label: "Contacted" },
  { value: "qualified", label: "Qualified" },
  { value: "sampling", label: "Sampling" },
  { value: "converted", label: "Converted" },
  { value: "disqualified", label: "Disqualified" },
];

const PIPELINE_STATUSES = STATUS_OPTIONS.map((option) => option.value);

const APPROACH_OPTIONS: Array<{ value: LeadApproach; label: string }> = [
  { value: "unknown", label: "Unknown" },
  { value: "call", label: "Call" },
  { value: "instagram", label: "Instagram" },
  { value: "email", label: "Email" },
  { value: "visit", label: "Visit" },
];

const PRIORITY_OPTIONS: Array<{ value: LeadPriority; label: string }> = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
];

const FIT_OPTIONS: Array<{ value: LeadFit; label: string }> = [
  { value: "unknown", label: "Unknown" },
  { value: "specialty", label: "Specialty roaster" },
  { value: "blend_dark_flavored", label: "Blends / dark / flavored" },
  { value: "multi_location", label: "Multi-location" },
  { value: "poor_fit", label: "Poor fit" },
];

const ACTIVITY_TYPE_OPTIONS: Array<{ value: ActivityType; label: string }> = [
  { value: "call", label: "Call" },
  { value: "email", label: "Email" },
  { value: "social", label: "Social" },
  { value: "sample", label: "Sample" },
  { value: "note", label: "Note" },
  { value: "status_change", label: "Status change" },
  { value: "account_created", label: "Account created" },
];

const OUTCOME_OPTIONS: Array<{ value: ActivityOutcome; label: string }> = [
  { value: "connected", label: "Connected" },
  { value: "left_voicemail", label: "Left voicemail" },
  { value: "no_answer", label: "No answer" },
  { value: "interested", label: "Interested" },
  { value: "not_interested", label: "Not interested" },
  { value: "needs_follow_up", label: "Needs follow-up" },
  { value: "sample_requested", label: "Sample requested" },
  { value: "account_created", label: "Account created" },
];

const STATUS_TONES: Record<LeadStatus, string> = {
  new: BADGE_TONES.slate,
  assigned: BADGE_TONES.sky,
  contacted: BADGE_TONES.blue,
  qualified: BADGE_TONES.violet,
  sampling: BADGE_TONES.amber,
  converted: BADGE_TONES.emerald,
  disqualified: BADGE_TONES.zinc,
};

const PRIORITY_TONES: Record<LeadPriority, string> = {
  low: BADGE_TONES.zinc,
  medium: BADGE_TONES.sky,
  high: BADGE_TONES.orange,
};

function getOptionLabel<T extends string>(options: Array<{ value: T; label: string }>, value: T): string {
  return options.find((option) => option.value === value)?.label ?? value;
}

function getStartOfTodayMs(): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today.getTime();
}

function getFollowUpBucket(lead: CrmLead): FollowUpBucket {
  if (!lead.nextFollowUpAt) return "noFollowUp";
  const today = getStartOfTodayMs();
  const nextWeek = today + 7 * 24 * 60 * 60 * 1000;
  if (lead.nextFollowUpAt < today) return "overdue";
  if (lead.nextFollowUpAt <= nextWeek) return "thisWeek";
  return "later";
}

function sortLeadsByFollowUp(leads: CrmLead[]): CrmLead[] {
  return [...leads].sort((left, right) => {
    const leftFollowUp = left.nextFollowUpAt ?? Number.MAX_SAFE_INTEGER;
    const rightFollowUp = right.nextFollowUpAt ?? Number.MAX_SAFE_INTEGER;
    if (leftFollowUp !== rightFollowUp) return leftFollowUp - rightFollowUp;
    return left.companyName.localeCompare(right.companyName);
  });
}

function createEmptyLeadDraft(): LeadDraft {
  return {
    companyName: "",
    contactName: "",
    email: "",
    phone: "",
    website: "",
    instagram: "",
    addressLine1: "",
    city: "",
    state: "",
    postalCode: "",
    shippingDistanceDays: "1",
    source: "Copilot search",
    status: "new",
    approach: "call",
    priority: "medium",
    fit: "unknown",
    tags: "",
    notes: "",
    assignedToUserProfileId: UNASSIGNED_VALUE,
    nextFollowUpAt: "",
  };
}

function leadToDraft(lead: CrmLead): LeadDraft {
  return {
    companyName: lead.companyName,
    contactName: lead.contactName ?? "",
    email: lead.email ?? "",
    phone: lead.phone ?? "",
    website: lead.website ?? "",
    instagram: lead.instagram ?? "",
    addressLine1: lead.addressLine1 ?? "",
    city: lead.city ?? "",
    state: lead.state ?? "",
    postalCode: lead.postalCode ?? "",
    shippingDistanceDays: lead.shippingDistanceDays ? String(lead.shippingDistanceDays) : "",
    source: lead.source ?? "",
    status: lead.status,
    approach: lead.approach,
    priority: lead.priority,
    fit: lead.fit,
    tags: lead.tags?.join(", ") ?? "",
    notes: lead.notes ?? "",
    assignedToUserProfileId: lead.assignedToUserProfileId ?? UNASSIGNED_VALUE,
    nextFollowUpAt: lead.nextFollowUpAt ? timestampToLocalDateInputString(lead.nextFollowUpAt) : "",
  };
}

function cleanOptional(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed || undefined;
}

function parseTags(value: string): string[] | undefined {
  const tags = value
    .split(",")
    .map((tag) => tag.trim())
    .filter(Boolean);
  return tags.length > 0 ? tags : undefined;
}

function parseOptionalNumber(value: string, label: string): number | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) throw new Error(`${label} must be a number`);
  return parsed;
}

function compactPayload<T extends Record<string, unknown>>(payload: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined),
  ) as Partial<T>;
}

function buildLeadPayload(draft: LeadDraft, options: { includeAssignment?: boolean } = {}) {
  const companyName = draft.companyName.trim();
  if (!companyName) throw new Error("Company name is required");

  const assignedToUserProfileId =
    draft.assignedToUserProfileId === UNASSIGNED_VALUE ? undefined : draft.assignedToUserProfileId;

  return compactPayload({
    companyName,
    contactName: cleanOptional(draft.contactName),
    email: cleanOptional(draft.email),
    phone: cleanOptional(draft.phone),
    website: cleanOptional(draft.website),
    instagram: cleanOptional(draft.instagram),
    addressLine1: cleanOptional(draft.addressLine1),
    city: cleanOptional(draft.city),
    state: cleanOptional(draft.state),
    postalCode: cleanOptional(draft.postalCode),
    shippingDistanceDays: parseOptionalNumber(draft.shippingDistanceDays, "Shipping days"),
    source: cleanOptional(draft.source),
    status: draft.status,
    approach: draft.approach,
    priority: draft.priority,
    fit: draft.fit,
    tags: parseTags(draft.tags),
    notes: cleanOptional(draft.notes),
    assignedToUserProfileId: options.includeAssignment === false ? undefined : assignedToUserProfileId,
    nextFollowUpAt: draft.nextFollowUpAt
      ? dateInputStringToLocalStartOfDayMs(draft.nextFollowUpAt)
      : undefined,
  });
}

function buildLeadUpdatePayload(draft: LeadDraft) {
  const companyName = draft.companyName.trim();
  if (!companyName) throw new Error("Company name is required");

  return compactPayload({
    companyName,
    contactName: draft.contactName,
    email: draft.email,
    phone: draft.phone,
    website: draft.website,
    instagram: draft.instagram,
    addressLine1: draft.addressLine1,
    city: draft.city,
    state: draft.state,
    postalCode: draft.postalCode,
    shippingDistanceDays: parseOptionalNumber(draft.shippingDistanceDays, "Shipping days"),
    source: draft.source,
    status: draft.status,
    approach: draft.approach,
    priority: draft.priority,
    fit: draft.fit,
    tags: parseTags(draft.tags) ?? [],
    notes: draft.notes,
    nextFollowUpAt: draft.nextFollowUpAt
      ? dateInputStringToLocalStartOfDayMs(draft.nextFollowUpAt)
      : undefined,
  });
}

function formatLocation(lead: CrmLead): string {
  return [lead.city, lead.state, lead.postalCode].filter(Boolean).join(", ");
}

function formatOptionalDate(value: number | undefined): string {
  if (!value) return "—";
  return formatDate(value, { month: "short", day: "numeric", year: "numeric" });
}

function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let inQuotes = false;

  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];
    const nextChar = text[index + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        cell += '"';
        index += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === "," && !inQuotes) {
      row.push(cell.trim());
      cell = "";
      continue;
    }

    if ((char === "\n" || char === "\r") && !inQuotes) {
      if (char === "\r" && nextChar === "\n") index += 1;
      row.push(cell.trim());
      if (row.some(Boolean)) rows.push(row);
      row = [];
      cell = "";
      continue;
    }

    cell += char;
  }

  row.push(cell.trim());
  if (row.some(Boolean)) rows.push(row);
  return rows;
}

function expandWholeLineQuotedRows(rows: string[][]): string[][] {
  return rows.map((row) => {
    const onlyCell = row.length === 1 ? row[0] : undefined;
    if (!onlyCell?.includes(",")) return row;

    const expandedRows = parseCsv(onlyCell);
    const expandedRow = expandedRows.length === 1 ? expandedRows[0] : undefined;
    return expandedRow && expandedRow.length > 1 ? expandedRow : row;
  });
}

function normalizeHeader(value: string): string {
  return value.trim().toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}

const CSV_FIELD_ALIASES: Record<string, keyof LeadDraft> = {
  company: "companyName",
  "company name": "companyName",
  roaster: "companyName",
  name: "companyName",
  contact: "contactName",
  "contact name": "contactName",
  person: "contactName",
  email: "email",
  phone: "phone",
  telephone: "phone",
  website: "website",
  url: "website",
  instagram: "instagram",
  social: "instagram",
  address: "addressLine1",
  street: "addressLine1",
  "address line 1": "addressLine1",
  city: "city",
  state: "state",
  zip: "postalCode",
  zipcode: "postalCode",
  "zip code": "postalCode",
  postal: "postalCode",
  "postal code": "postalCode",
  status: "status",
  approach: "approach",
  priority: "priority",
  fit: "fit",
  notes: "notes",
  note: "notes",
  source: "source",
  "shipping days": "shippingDistanceDays",
  "ship days": "shippingDistanceDays",
  "shipping distance days": "shippingDistanceDays",
  tags: "tags",
};

interface CsvImportIssue {
  row?: number;
  field?: string;
  message: string;
}

interface CsvImportPreview {
  leads: LeadDraft[];
  requiredErrors: CsvImportIssue[];
  optionErrors: CsvImportIssue[];
  warnings: CsvImportIssue[];
}

const CSV_RECOMMENDED_COLUMNS: Array<{ field: keyof LeadDraft; label: string }> = [
  { field: "addressLine1", label: "Address" },
  { field: "city", label: "City" },
  { field: "state", label: "State" },
  { field: "postalCode", label: "Zip" },
];

const CSV_OPTION_FIELDS = new Set<keyof LeadDraft>(["status", "approach", "priority", "fit"]);

function formatCsvIssue(issue: CsvImportIssue): string {
  const parts = [
    issue.row ? `Row ${issue.row}` : null,
    issue.field ? issue.field : null,
  ].filter(Boolean);
  return parts.length > 0 ? `${parts.join(" • ")}: ${issue.message}` : issue.message;
}

function normalizeOptionLabel(value: string): string {
  return value.trim().toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}

function parseOptionValue<T extends string>(
  value: string,
  options: Array<{ value: T; label: string }>,
): T | null {
  const normalized = normalizeOptionLabel(value);
  if (!normalized) return null;
  return options.find(
    (option) => normalizeOptionLabel(option.value) === normalized || normalizeOptionLabel(option.label) === normalized,
  )?.value ?? null;
}

function getOptionsForCsvField(field: keyof LeadDraft): Array<{ value: string; label: string }> | null {
  if (field === "status") return STATUS_OPTIONS;
  if (field === "approach") return APPROACH_OPTIONS;
  if (field === "priority") return PRIORITY_OPTIONS;
  if (field === "fit") return FIT_OPTIONS;
  return null;
}

function validateLeadCsv(text: string): CsvImportPreview {
  const rows = expandWholeLineQuotedRows(parseCsv(text));
  const preview: CsvImportPreview = {
    leads: [],
    requiredErrors: [],
    optionErrors: [],
    warnings: [],
  };

  if (rows.length < 2) {
    preview.requiredErrors.push({
      message: "CSV must include a header row and at least one lead row.",
    });
    return preview;
  }

  const headerRow = rows[0];
  if (!headerRow) {
    preview.requiredErrors.push({ message: "CSV header row is required." });
    return preview;
  }

  const headers = headerRow.map((header) => CSV_FIELD_ALIASES[normalizeHeader(header)]);
  const unknownHeaders = headerRow.filter((header, index) => header.trim() && !headers[index]);

  if (!headers.includes("companyName")) {
    preview.requiredErrors.push({
      field: "Roaster / Company",
      message: "Required column missing. Use Roaster, Company, Company Name, or Name.",
    });
    return preview;
  }

  for (const recommended of CSV_RECOMMENDED_COLUMNS) {
    if (!headers.includes(recommended.field)) {
      preview.warnings.push({
        field: recommended.label,
        message: "Recommended column missing. Import can continue, but the lead will have less location context.",
      });
    }
  }

  if (unknownHeaders.length > 0) {
    preview.warnings.push({
      message: `Ignored unknown column${unknownHeaders.length === 1 ? "" : "s"}: ${unknownHeaders.join(", ")}.`,
    });
  }

  rows.slice(1).forEach((row, rowIndex) => {
    const csvRowNumber = rowIndex + 2;
    if (!row.some((cell) => cell.trim())) return;

    const draft = createEmptyLeadDraft();
    row.forEach((cell, index) => {
      const field = headers[index];
      if (!field) return;

      if (CSV_OPTION_FIELDS.has(field)) {
        if (!cell.trim()) return;
        const options = getOptionsForCsvField(field);
        const parsedValue = options ? parseOptionValue(cell, options) : null;
        if (!parsedValue) {
          preview.optionErrors.push({
            row: csvRowNumber,
            field,
            message: `Unsupported option "${cell}". Use one of: ${options?.map((option) => option.label).join(", ")}.`,
          });
          return;
        }
        if (field === "status") draft.status = parsedValue as LeadStatus;
        if (field === "approach") draft.approach = parsedValue as LeadApproach;
        if (field === "priority") draft.priority = parsedValue as LeadPriority;
        if (field === "fit") draft.fit = parsedValue as LeadFit;
        return;
      }

      switch (field) {
        case "companyName":
          draft.companyName = cell;
          break;
        case "contactName":
          draft.contactName = cell;
          break;
        case "email":
          draft.email = cell;
          break;
        case "phone":
          draft.phone = cell;
          break;
        case "website":
          draft.website = cell;
          break;
        case "instagram":
          draft.instagram = cell;
          break;
        case "addressLine1":
          draft.addressLine1 = cell;
          break;
        case "city":
          draft.city = cell;
          break;
        case "state":
          draft.state = cell;
          break;
        case "postalCode":
          draft.postalCode = cell;
          break;
        case "shippingDistanceDays":
          draft.shippingDistanceDays = cell;
          break;
        case "source":
          draft.source = cell;
          break;
        case "notes":
          draft.notes = cell;
          break;
        case "tags":
          draft.tags = cell;
          break;
        case "nextFollowUpAt":
          draft.nextFollowUpAt = cell;
          break;
        default:
          break;
      }
    });

    if (!draft.companyName.trim()) {
      preview.requiredErrors.push({
        row: csvRowNumber,
        field: "Roaster / Company",
        message: "Company name is required.",
      });
      return;
    }

    preview.leads.push(draft);
  });

  if (preview.leads.length === 0 && preview.requiredErrors.length === 0) {
    preview.requiredErrors.push({ message: "No importable lead rows found." });
  }

  return preview;
}

function parseLeadCsv(text: string): LeadDraft[] {
  const preview = validateLeadCsv(text);
  const blockingErrors = [...preview.requiredErrors, ...preview.optionErrors];
  if (blockingErrors.length > 0) {
    throw new Error(formatCsvIssue(blockingErrors[0]!));
  }
  return preview.leads;
}

function StatCard({
  title,
  value,
  description,
  icon: Icon,
}: {
  title: string;
  value: string | number;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
}) {
  return (
    <Card className="rounded-xl border-border/70">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 px-3 pb-2 pt-3">
        <CardDescription className="text-xs">{title}</CardDescription>
        <Icon className="h-3.5 w-3.5 text-muted-foreground" />
      </CardHeader>
      <CardContent className="px-3 pb-3">
        <div className="text-xl font-semibold tabular-nums">{value}</div>
        <p className="mt-0.5 truncate text-xs text-muted-foreground">{description}</p>
      </CardContent>
    </Card>
  );
}

function StatCardSkeleton() {
  return (
    <Card className="rounded-xl border-border/70">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 px-3 pb-2 pt-3">
        <Skeleton className="h-3 w-24" />
        <Skeleton className="h-3.5 w-3.5 rounded-full" />
      </CardHeader>
      <CardContent className="px-3 pb-3">
        <Skeleton className="h-6 w-14" />
        <Skeleton className="mt-2 h-3 w-32" />
      </CardContent>
    </Card>
  );
}

function MetricGridSkeleton() {
  return (
    <div className="grid gap-3 md:grid-cols-4">
      {Array.from({ length: 4 }).map((_, index) => (
        <StatCardSkeleton key={index} />
      ))}
    </div>
  );
}

function SelectField<T extends string>({
  label,
  value,
  options,
  onValueChange,
}: {
  label: string;
  value: T;
  options: Array<{ value: T; label: string }>;
  onValueChange: (value: T) => void;
}) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Select value={value} onValueChange={(nextValue) => onValueChange(nextValue as T)}>
        <SelectTrigger className="h-9 w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function EditableLeadSelectCell<T extends string>({
  lead,
  field,
  value,
  options,
  authUserId,
  toneByValue,
}: {
  lead: CrmLead;
  field: "status" | "priority" | "approach" | "fit";
  value: T;
  options: Array<{ value: T; label: string }>;
  authUserId?: string;
  toneByValue?: Partial<Record<T, string>>;
}) {
  const [open, setOpen] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const updateLead = useMutation(api.crm.updateLead);

  const handleChange = React.useCallback(
    async (nextValue: string) => {
      setOpen(false);
      if (nextValue === value || !authUserId) return;
      setSaving(true);
      try {
        await updateLead({
          authUserId,
          leadId: lead._id,
          patch: { [field]: nextValue },
        });
      } catch (error) {
        toast.error(error instanceof Error ? error.message : "Failed to update lead");
      } finally {
        setSaving(false);
      }
    },
    [authUserId, field, lead._id, updateLead, value],
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={saving || !authUserId}>
        <button type="button" className="cursor-pointer rounded-md focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring">
          <Badge variant="outline" className={cn(toneByValue?.[value] ?? BADGE_TONES.slate, "cursor-pointer")}>{getOptionLabel(options, value)}</Badge>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-48 p-0" align="start">
        <Command>
          <CommandInput placeholder="Search options..." />
          <CommandList className="max-h-full">
            <CommandEmpty>No options found.</CommandEmpty>
            <CommandGroup className="max-h-[260px] scroll-py-1 overflow-y-auto overflow-x-hidden">
              {options.map((option) => {
                const isCurrent = option.value === value;
                return (
                  <CommandItem key={option.value} onSelect={() => void handleChange(option.value)}>
                    <div className={cn("flex size-4 items-center justify-center rounded-sm border border-primary", isCurrent ? "bg-primary text-primary-foreground" : "opacity-50 [&_svg]:invisible")}>
                      <Check className="size-3" />
                    </div>
                    <Badge variant="outline" className={cn(toneByValue?.[option.value] ?? BADGE_TONES.slate, "text-xs")}>{option.label}</Badge>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function LeadAssigneeCell({
  lead,
  assignees,
  authUserId,
  canAssign,
}: {
  lead: CrmLead;
  assignees: SalesAssignee[];
  authUserId?: string;
  canAssign: boolean;
}) {
  const [open, setOpen] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const assignLead = useMutation(api.crm.assignLead);
  const currentValue = lead.assignedToUserProfileId ?? UNASSIGNED_VALUE;

  const handleAssign = React.useCallback(
    async (nextValue: string) => {
      setOpen(false);
      if (nextValue === currentValue || !authUserId || !canAssign) return;
      setSaving(true);
      try {
        await assignLead({
          authUserId,
          leadId: lead._id,
          assignedToUserProfileId: nextValue === UNASSIGNED_VALUE ? undefined : nextValue,
        });
      } catch (error) {
        toast.error(error instanceof Error ? error.message : "Failed to assign lead");
      } finally {
        setSaving(false);
      }
    },
    [assignLead, authUserId, canAssign, currentValue, lead._id],
  );

  if (!canAssign) {
    return <span className="text-sm">{lead.assignedToName ?? "—"}</span>;
  }

  const currentLabel = currentValue === UNASSIGNED_VALUE ? "Unassigned" : (lead.assignedToName ?? "Unassigned");

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={saving || !authUserId}>
        <button type="button" className="cursor-pointer rounded-md focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring">
          <Badge variant="outline" className={cn(BADGE_TONES.slate, "cursor-pointer")}>{currentLabel}</Badge>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-0" align="start">
        <Command>
          <CommandInput placeholder="Search owners..." />
          <CommandList className="max-h-full">
            <CommandEmpty>No owners found.</CommandEmpty>
            <CommandGroup className="max-h-[300px] scroll-py-1 overflow-y-auto overflow-x-hidden">
              {[{ _id: UNASSIGNED_VALUE, name: "Unassigned", email: "" }, ...assignees].map((assignee) => {
                const isCurrent = currentValue === assignee._id;
                return (
                  <CommandItem key={assignee._id} onSelect={() => void handleAssign(assignee._id)}>
                    <div className={cn("flex size-4 items-center justify-center rounded-sm border border-primary", isCurrent ? "bg-primary text-primary-foreground" : "opacity-50 [&_svg]:invisible")}>
                      <Check className="size-3" />
                    </div>
                    <Badge variant="outline" className={cn(BADGE_TONES.slate, "text-xs")}>{assignee.name ?? assignee.email}</Badge>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function useLeadTableColumns({
  data,
  assignees,
  authUserId,
  canAssign,
  onOpenLead,
}: {
  data: CrmLead[];
  assignees: SalesAssignee[];
  authUserId?: string;
  canAssign: boolean;
  onOpenLead: (leadId: string) => void;
}): ColumnDef<CrmLead>[] {
  const sourceOptions = React.useMemo(
    () => [...new Set(data.map((lead) => lead.source).filter(Boolean))]
      .sort()
      .map((source) => ({ label: source!, value: source! })),
    [data],
  );
  const assigneeOptions = React.useMemo(
    () => [
      { label: "Unassigned", value: UNASSIGNED_VALUE },
      ...assignees.map((assignee) => ({ label: assignee.name ?? assignee.email ?? "Unknown", value: assignee._id })),
    ],
    [assignees],
  );

  return React.useMemo<ColumnDef<CrmLead>[]>(() => [
    {
      id: "actions",
      header: "Action",
      cell: ({ row }) => (
        <Button type="button" variant="outline" size="xs" className="h-7 px-2 text-xs" onClick={() => onOpenLead(row.original._id)}>
          Open
          <span className="sr-only"> {row.original.companyName}</span>
        </Button>
      ),
      size: 72,
      minSize: 72,
      maxSize: 72,
      enableSorting: false,
      enableHiding: false,
    },
    {
      id: "companyName",
      accessorFn: (lead) => lead.companyName,
      header: ({ column }) => <DataTableColumnHeader column={column} label="Roaster" />,
      cell: ({ row }) => {
        const lead = row.original;
        return (
          <div className="max-w-[280px] text-left">
            <span className="block truncate text-sm font-medium text-foreground">{lead.companyName}</span>
            <span className="block truncate text-xs text-muted-foreground">{formatLocation(lead) || lead.website || "No location"}</span>
          </div>
        );
      },
      meta: { label: "Roaster", variant: "text" as const, placeholder: "Search roaster..." },
      enableColumnFilter: true,
      filterFn: (row, _columnId, filterValue: string) => {
        if (!filterValue) return true;
        const search = String(filterValue).toLowerCase();
        const lead = row.original;
        return [lead.companyName, lead.city, lead.state, lead.postalCode, lead.addressLine1]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(search);
      },
    },
    {
      accessorKey: "status",
      header: ({ column }) => <DataTableColumnHeader column={column} label="Status" />,
      cell: ({ row }) => (
        <EditableLeadSelectCell
          lead={row.original}
          field="status"
          value={row.original.status}
          options={STATUS_OPTIONS}
          authUserId={authUserId}
          toneByValue={STATUS_TONES}
        />
      ),
      meta: { label: "Status", variant: "select" as const, options: STATUS_OPTIONS },
      enableColumnFilter: true,
      filterFn: (row, _columnId, filterValues: string[]) => filterValues.some((value) => value === row.original.status),
    },
    {
      accessorKey: "assignedToUserProfileId",
      header: ({ column }) => <DataTableColumnHeader column={column} label="Owner" />,
      cell: ({ row }) => (
        <LeadAssigneeCell lead={row.original} assignees={assignees} authUserId={authUserId} canAssign={canAssign} />
      ),
      meta: { label: "Owner", variant: "select" as const, options: assigneeOptions },
      enableColumnFilter: true,
      filterFn: (row, _columnId, filterValues: string[]) => {
        const assignee = row.original.assignedToUserProfileId ?? UNASSIGNED_VALUE;
        return filterValues.some((value) => value === assignee);
      },
    },
    {
      accessorKey: "priority",
      header: ({ column }) => <DataTableColumnHeader column={column} label="Priority" />,
      cell: ({ row }) => (
        <EditableLeadSelectCell
          lead={row.original}
          field="priority"
          value={row.original.priority}
          options={PRIORITY_OPTIONS}
          authUserId={authUserId}
          toneByValue={PRIORITY_TONES}
        />
      ),
      meta: { label: "Priority", variant: "select" as const, options: PRIORITY_OPTIONS },
      enableColumnFilter: true,
      filterFn: (row, _columnId, filterValues: string[]) => filterValues.some((value) => value === row.original.priority),
    },
    {
      accessorKey: "approach",
      header: ({ column }) => <DataTableColumnHeader column={column} label="Approach" />,
      cell: ({ row }) => (
        <EditableLeadSelectCell lead={row.original} field="approach" value={row.original.approach} options={APPROACH_OPTIONS} authUserId={authUserId} />
      ),
      meta: { label: "Approach", variant: "select" as const, options: APPROACH_OPTIONS },
      enableColumnFilter: true,
      filterFn: (row, _columnId, filterValues: string[]) => filterValues.some((value) => value === row.original.approach),
    },
    {
      accessorKey: "fit",
      header: ({ column }) => <DataTableColumnHeader column={column} label="Fit" />,
      cell: ({ row }) => (
        <EditableLeadSelectCell lead={row.original} field="fit" value={row.original.fit} options={FIT_OPTIONS} authUserId={authUserId} />
      ),
      meta: { label: "Fit", variant: "select" as const, options: FIT_OPTIONS },
      enableColumnFilter: true,
      filterFn: (row, _columnId, filterValues: string[]) => filterValues.some((value) => value === row.original.fit),
    },
    {
      id: "nextFollowUpAt",
      accessorFn: (lead) => lead.nextFollowUpAt ?? undefined,
      header: ({ column }) => <DataTableColumnHeader column={column} label="Follow-up" />,
      cell: ({ row }) => formatOptionalDate(row.original.nextFollowUpAt),
      meta: { label: "Follow-up", variant: "dateRange" as const },
      enableColumnFilter: true,
    },
    {
      id: "lastContactedAt",
      accessorFn: (lead) => lead.lastContactedAt ?? undefined,
      header: ({ column }) => <DataTableColumnHeader column={column} label="Last touch" />,
      cell: ({ row }) => formatOptionalDate(row.original.lastContactedAt),
      meta: { label: "Last touch", variant: "dateRange" as const },
      enableColumnFilter: true,
    },
    {
      id: "source",
      accessorFn: (lead) => lead.source ?? undefined,
      header: ({ column }) => <DataTableColumnHeader column={column} label="Source" />,
      cell: ({ row }) => row.original.source ? <Badge variant="outline" className={BADGE_TONES.slate}>{row.original.source}</Badge> : "—",
      meta: { label: "Source", variant: "select" as const, options: sourceOptions },
      enableColumnFilter: true,
      filterFn: (row, _columnId, filterValues: string[]) => filterValues.some((value) => value === row.original.source),
    },
    {
      id: "tags",
      accessorFn: (lead) => (lead.tags ?? []).join(", "),
      header: ({ column }) => <DataTableColumnHeader column={column} label="Tags" />,
      cell: ({ row }) => {
        const allTags = row.original.tags ?? [];
        const tags = allTags.slice(0, 3);
        return tags.length ? (
          <div className="flex max-w-[260px] flex-wrap gap-1">
            {tags.map((tag) => <Badge key={tag} variant="outline" className={cn(pickLeadTagTone(tag), "text-xs")}>{tag}</Badge>)}
            {allTags.length > tags.length ? <Badge variant="outline" className="text-xs">+{allTags.length - tags.length}</Badge> : null}
          </div>
        ) : "—";
      },
      meta: { label: "Tags", variant: "select" as const, options: LEAD_TAG_OPTIONS.map((tag) => ({ label: tag, value: tag })) },
      enableColumnFilter: true,
      filterFn: (row, _columnId, filterValues: string[]) => filterValues.some((value) => (row.original.tags ?? []).some((tag) => tag.toLowerCase() === value.toLowerCase())),
    },
  ], [assigneeOptions, assignees, authUserId, canAssign, onOpenLead, sourceOptions]);
}

function LeadTagsField({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const selectedTags = React.useMemo(() => value.split(",").map((tag) => tag.trim()).filter(Boolean), [value]);
  const toggleTag = React.useCallback((tag: string) => {
    const normalized = tag.toLowerCase();
    const exists = selectedTags.some((item) => item.toLowerCase() === normalized);
    const nextTags = exists ? selectedTags.filter((item) => item.toLowerCase() !== normalized) : [...selectedTags, tag];
    onChange(nextTags.join(", "));
  }, [onChange, selectedTags]);

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {LEAD_TAG_OPTIONS.map((tag) => {
          const selected = selectedTags.some((item) => item.toLowerCase() === tag.toLowerCase());
          return (
            <button
              key={tag}
              type="button"
              onClick={() => toggleTag(tag)}
              aria-pressed={selected}
              className={cn(
                "inline-flex h-7 items-center gap-1.5 rounded-full border px-2.5 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
                selected ? pickLeadTagTone(tag) : "border-border bg-background text-muted-foreground hover:bg-muted/50 hover:text-foreground",
              )}
            >
              <Tags className="h-3 w-3" />
              {tag}
            </button>
          );
        })}
      </div>
      <Input value={value} onChange={(event) => onChange(event.target.value)} placeholder="Add custom tags, comma separated" />
    </div>
  );
}

function DatePickerField({ id, value, onChange }: { id: string; value: string; onChange: (value: string) => void }) {
  const selectedDate = React.useMemo(() => value ? new Date(`${value}T00:00:00`) : undefined, [value]);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button id={id} type="button" variant="outline" className="h-9 w-full justify-start bg-background text-left font-normal shadow-none">
          <CalendarClock className="h-4 w-4 text-muted-foreground" />
          {selectedDate ? formatDate(selectedDate.getTime()) : <span className="text-muted-foreground">Choose date</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar mode="single" selected={selectedDate} onSelect={(date) => onChange(date ? timestampToLocalDateInputString(date.getTime()) : "")} />
      </PopoverContent>
    </Popover>
  );
}

function LeadFormFields({
  draft,
  setDraft,
  assignees,
  showAssignee,
}: {
  draft: LeadDraft;
  setDraft: React.Dispatch<React.SetStateAction<LeadDraft>>;
  assignees: SalesAssignee[];
  showAssignee: boolean;
}) {
  const updateField = React.useCallback(
    (field: keyof LeadDraft, value: string) => setDraft((current) => ({ ...current, [field]: value })),
    [setDraft],
  );
  return (
    <div className="space-y-8 [&_input]:h-9 [&_input]:shadow-none [&_label]:text-xs [&_label]:font-medium [&_label]:text-muted-foreground [&_textarea]:min-h-20 [&_textarea]:shadow-none [button[data-slot=select-trigger]]:shadow-none">
      <section className="space-y-4">
        <div className="flex items-center gap-2 border-b pb-2">
          <Phone className="h-3.5 w-3.5 text-muted-foreground" />
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Contact</p>
        </div>
        <div className="grid gap-x-5 gap-y-5 md:grid-cols-2">
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="lead-company">Company / roaster</Label>
            <Input id="lead-company" value={draft.companyName} onChange={(event) => updateField("companyName", event.target.value)} placeholder="Example Roasters" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="lead-contact">Contact</Label>
            <Input id="lead-contact" value={draft.contactName} onChange={(event) => updateField("contactName", event.target.value)} placeholder="Name if known" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="lead-phone">Phone</Label>
            <Input id="lead-phone" value={draft.phone} onChange={(event) => updateField("phone", event.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="lead-email">Email</Label>
            <Input id="lead-email" type="email" value={draft.email} onChange={(event) => updateField("email", event.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="lead-website">Website</Label>
            <Input id="lead-website" value={draft.website} onChange={(event) => updateField("website", event.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="lead-instagram">Instagram / social</Label>
            <Input id="lead-instagram" value={draft.instagram} onChange={(event) => updateField("instagram", event.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="lead-address">Address</Label>
            <Input id="lead-address" value={draft.addressLine1} onChange={(event) => updateField("addressLine1", event.target.value)} />
          </div>
          <div className="grid gap-5 md:col-span-2 md:grid-cols-[1fr_80px_104px_104px]">
            <div className="space-y-2">
              <Label htmlFor="lead-city">City</Label>
              <Input id="lead-city" value={draft.city} onChange={(event) => updateField("city", event.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lead-state">State</Label>
              <Input id="lead-state" value={draft.state} onChange={(event) => updateField("state", event.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lead-zip">Zip</Label>
              <Input id="lead-zip" value={draft.postalCode} onChange={(event) => updateField("postalCode", event.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lead-days">Ship days</Label>
              <Input id="lead-days" inputMode="decimal" value={draft.shippingDistanceDays} onChange={(event) => updateField("shippingDistanceDays", event.target.value)} />
            </div>
          </div>
        </div>
      </section>

      <section className="space-y-4">
        <div className="flex items-center gap-2 border-b pb-2">
          <CircleDot className="h-3.5 w-3.5 text-muted-foreground" />
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Qualification</p>
        </div>
        <div className="grid gap-x-5 gap-y-5 md:grid-cols-2">
          <SelectField label="Status" value={draft.status} options={STATUS_OPTIONS} onValueChange={(value) => updateField("status", value)} />
          <SelectField label="Approach" value={draft.approach} options={APPROACH_OPTIONS} onValueChange={(value) => updateField("approach", value)} />
          <SelectField label="Priority" value={draft.priority} options={PRIORITY_OPTIONS} onValueChange={(value) => updateField("priority", value)} />
          <SelectField label="Coffee fit" value={draft.fit} options={FIT_OPTIONS} onValueChange={(value) => updateField("fit", value)} />
          {showAssignee ? (
            <div className="space-y-2">
              <Label>Assigned to</Label>
              <Select value={draft.assignedToUserProfileId} onValueChange={(value) => updateField("assignedToUserProfileId", value)}>
                <SelectTrigger className="h-9 w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={UNASSIGNED_VALUE}>Unassigned</SelectItem>
                  {assignees.map((assignee) => (
                    <SelectItem key={assignee._id} value={assignee._id}>{assignee.name ?? assignee.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}
          <div className="space-y-2">
            <Label htmlFor="lead-follow-up">Next follow-up</Label>
            <DatePickerField id="lead-follow-up" value={draft.nextFollowUpAt} onChange={(value) => updateField("nextFollowUpAt", value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="lead-source" className="flex items-center gap-1.5"><Hash className="h-3.5 w-3.5" /> Source</Label>
            <Input id="lead-source" value={draft.source} onChange={(event) => updateField("source", event.target.value)} placeholder="DFW Metro, Houston Metro, referral..." />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="lead-tags" className="flex items-center gap-1.5"><Tags className="h-3.5 w-3.5" /> Tags</Label>
            <LeadTagsField value={draft.tags} onChange={(value) => updateField("tags", value)} />
          </div>
        </div>
      </section>

      <section className="space-y-4">
        <div className="flex items-center gap-2 border-b pb-2">
          <MessageSquarePlus className="h-3.5 w-3.5 text-muted-foreground" />
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Notes</p>
        </div>
        <Textarea id="lead-notes" value={draft.notes} onChange={(event) => updateField("notes", event.target.value)} placeholder="Menu notes, current supplier, what to listen for on the call..." className="min-h-20" />
      </section>
    </div>
  );
}

function CreateLeadDialog({
  open,
  onOpenChange,
  assignees,
  canAssign,
  authUserId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  assignees: SalesAssignee[];
  canAssign: boolean;
  authUserId?: string;
}) {
  const [draft, setDraft] = React.useState<LeadDraft>(() => createEmptyLeadDraft());
  const [saving, setSaving] = React.useState(false);
  const createLead = useMutation(api.crm.createLead);

  React.useEffect(() => {
    if (!open) setDraft(createEmptyLeadDraft());
  }, [open]);

  const handleSubmit = React.useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setSaving(true);
      try {
        await createLead({ authUserId, lead: buildLeadPayload(draft) });
        toast.success("CRM lead created");
        onOpenChange(false);
      } catch (error) {
        toast.error(error instanceof Error ? error.message : "Failed to create CRM lead");
      } finally {
        setSaving(false);
      }
    },
    [authUserId, createLead, draft, onOpenChange],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
        <form onSubmit={(event) => void handleSubmit(event)} className="space-y-6">
          <DialogHeader>
            <DialogTitle>Add CRM lead</DialogTitle>
            <DialogDescription>
              Create a prospect record for roasters that are not current clients yet.
            </DialogDescription>
          </DialogHeader>
          <LeadFormFields draft={draft} setDraft={setDraft} assignees={assignees} showAssignee={canAssign} />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? "Creating..." : "Create lead"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ImportLeadsDialog({
  open,
  onOpenChange,
  assignees,
  authUserId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  assignees: SalesAssignee[];
  authUserId?: string;
}) {
  const [csvText, setCsvText] = React.useState("");
  const [importSource, setImportSource] = React.useState("AI lead import");
  const [selectedFileName, setSelectedFileName] = React.useState("");
  const [assignedToUserProfileId, setAssignedToUserProfileId] = React.useState(UNASSIGNED_VALUE);
  const [saving, setSaving] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const importLeads = useMutation(api.crm.bulkImportLeads);

  React.useEffect(() => {
    if (!open) {
      setCsvText("");
      setImportSource("AI lead import");
      setSelectedFileName("");
      setAssignedToUserProfileId(UNASSIGNED_VALUE);
    }
  }, [open]);

  const parsedPreview = React.useMemo<CsvImportPreview>(() => {
    if (!csvText.trim()) {
      return { leads: [], requiredErrors: [], optionErrors: [], warnings: [] };
    }
    return validateLeadCsv(csvText);
  }, [csvText]);
  const hasBlockingImportErrors = parsedPreview.requiredErrors.length > 0 || parsedPreview.optionErrors.length > 0;
  const importableLeadCount = parsedPreview.leads.length;

  const handleCsvFileChange = React.useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setCsvText(await file.text());
      setImportSource(file.name.replace(/\.[^.]+$/, ""));
      setSelectedFileName(file.name);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to read CSV file");
    } finally {
      event.target.value = "";
    }
  }, []);

  const handleImport = React.useCallback(async () => {
    setSaving(true);
    try {
      if (hasBlockingImportErrors) {
        const firstIssue = parsedPreview.requiredErrors[0] ?? parsedPreview.optionErrors[0];
        throw new Error(firstIssue ? formatCsvIssue(firstIssue) : "Fix CSV errors before importing");
      }

      const batchSource = importSource.trim() || "AI lead import";
      const leads = parsedPreview.leads.map((draft) => buildLeadPayload({ ...draft, source: batchSource }));
      const result = await importLeads({
        authUserId,
        leads,
        assignedToUserProfileId:
          assignedToUserProfileId === UNASSIGNED_VALUE ? undefined : assignedToUserProfileId,
      });
      toast.success(`Imported ${result.created} leads${result.skipped ? `, skipped ${result.skipped}` : ""}`);
      onOpenChange(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to import CRM leads");
    } finally {
      setSaving(false);
    }
  }, [
    assignedToUserProfileId,
    authUserId,
    hasBlockingImportErrors,
    importLeads,
    importSource,
    onOpenChange,
    parsedPreview.leads,
    parsedPreview.optionErrors,
    parsedPreview.requiredErrors,
  ]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Import leads from CSV</DialogTitle>
          <DialogDescription>
            Upload or paste one clean CSV section at a time. Required: Roaster/Company. Recommended: Address, City, State, Zip. Optional: Email, Phone, Website, Instagram, Notes, Tags, Status, Approach, Priority, Fit.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="crm-import-source">Import source</Label>
              <Input
                id="crm-import-source"
                value={importSource}
                onChange={(event) => setImportSource(event.target.value)}
                placeholder="DFW Metro"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="crm-csv-file">CSV file</Label>
              <input
                ref={fileInputRef}
                id="crm-csv-file"
                type="file"
                accept=".csv,text/csv"
                className="sr-only"
                onChange={(event) => void handleCsvFileChange(event)}
              />
              <div className="flex min-h-9 items-center gap-3 rounded-[10px] border bg-background px-3 py-2">
                <Button type="button" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
                  Choose CSV
                </Button>
                <span className="truncate text-sm text-muted-foreground">
                  {selectedFileName || "No file selected"}
                </span>
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Assign imported leads to</Label>
            <Select value={assignedToUserProfileId} onValueChange={setAssignedToUserProfileId}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={UNASSIGNED_VALUE}>Leave unassigned</SelectItem>
                {assignees.map((assignee) => (
                  <SelectItem key={assignee._id} value={assignee._id}>
                    {assignee.name ?? assignee.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="crm-csv">CSV data</Label>
            <Textarea
              id="crm-csv"
              value={csvText}
              onChange={(event) => setCsvText(event.target.value)}
              className="h-48 max-h-[36vh] resize-y font-mono text-xs"
              placeholder="company,city,state,phone,website,notes"
            />
            <div className="space-y-2 text-xs">
              <p className={cn(hasBlockingImportErrors ? "text-destructive" : "text-muted-foreground")}>
                {csvText.trim()
                  ? hasBlockingImportErrors
                    ? "Fix required/option errors before importing. Warnings do not block import."
                    : `${importableLeadCount} leads ready to import from ${importSource.trim() || "AI lead import"}.`
                  : "Upload or paste CSV rows to preview import count."}
              </p>
              {parsedPreview.requiredErrors.length > 0 ? (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-red-900 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-200">
                  <p className="font-medium">Required fixes</p>
                  <ul className="mt-1 list-disc space-y-1 pl-4">
                    {parsedPreview.requiredErrors.slice(0, 5).map((issue, index) => (
                      <li key={`required-${index}`}>{formatCsvIssue(issue)}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {parsedPreview.optionErrors.length > 0 ? (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-red-900 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-200">
                  <p className="font-medium">Option fixes</p>
                  <ul className="mt-1 list-disc space-y-1 pl-4">
                    {parsedPreview.optionErrors.slice(0, 5).map((issue, index) => (
                      <li key={`option-${index}`}>{formatCsvIssue(issue)}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {parsedPreview.warnings.length > 0 ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-200">
                  <p className="font-medium">Warnings</p>
                  <ul className="mt-1 list-disc space-y-1 pl-4">
                    {parsedPreview.warnings.slice(0, 5).map((issue, index) => (
                      <li key={`warning-${index}`}>{formatCsvIssue(issue)}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={() => void handleImport()} disabled={saving || importableLeadCount === 0 || hasBlockingImportErrors}>
            {saving ? "Importing..." : "Import leads"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LeadDetail({
  lead,
  activities,
  assignees,
  canAssign,
  authUserId,
}: {
  lead: CrmLead | null;
  activities: CrmActivity[] | undefined;
  assignees: SalesAssignee[];
  canAssign: boolean;
  authUserId?: string;
}) {
  if (!lead) return null;

  return (
    <LeadDetailBody
      key={lead._id}
      lead={lead}
      activities={activities}
      assignees={assignees}
      canAssign={canAssign}
      authUserId={authUserId}
    />
  );
}

function LeadDetailBody({
  lead,
  activities,
  assignees,
  canAssign,
  authUserId,
}: {
  lead: CrmLead;
  activities: CrmActivity[] | undefined;
  assignees: SalesAssignee[];
  canAssign: boolean;
  authUserId?: string;
}) {
  const [draft, setDraft] = React.useState<LeadDraft>(() => leadToDraft(lead));
  const [saving, setSaving] = React.useState(false);
  const [activityType, setActivityType] = React.useState<ActivityType>("call");
  const [activityOutcome, setActivityOutcome] = React.useState<ActivityOutcome>("connected");
  const [activityStatus, setActivityStatus] = React.useState<string>(NO_STATUS_CHANGE_VALUE);
  const [activityNote, setActivityNote] = React.useState("");
  const [activityFollowUpAt, setActivityFollowUpAt] = React.useState("");
  const [logging, setLogging] = React.useState(false);
  const updateLead = useMutation(api.crm.updateLead);
  const assignLead = useMutation(api.crm.assignLead);
  const createActivity = useMutation(api.crm.createLeadActivity);
  const lastLeadSyncKeyRef = React.useRef(`${lead._id}:${lead.updatedAt}`);
  const savedDraft = React.useMemo(() => leadToDraft(lead), [lead]);
  const hasUnsavedChanges = React.useMemo(() => JSON.stringify(draft) !== JSON.stringify(savedDraft), [draft, savedDraft]);

  React.useEffect(() => {
    const syncKey = `${lead._id}:${lead.updatedAt}`;
    if (lastLeadSyncKeyRef.current === syncKey) return;

    lastLeadSyncKeyRef.current = syncKey;
    setDraft(leadToDraft(lead));
    setActivityStatus(NO_STATUS_CHANGE_VALUE);
    setActivityNote("");
    setActivityFollowUpAt("");
  }, [lead]);

  const handleSave = React.useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!lead) return;
      setSaving(true);
      try {
        await updateLead({ authUserId, leadId: lead._id, patch: buildLeadUpdatePayload(draft) });
        toast.success("Lead updated");
      } catch (error) {
        toast.error(error instanceof Error ? error.message : "Failed to update lead");
      } finally {
        setSaving(false);
      }
    },
    [authUserId, draft, lead, updateLead],
  );

  const handleAssign = React.useCallback(
    async (value: string) => {
      if (!lead) return;
      try {
        await assignLead({
          authUserId,
          leadId: lead._id,
          assignedToUserProfileId: value === UNASSIGNED_VALUE ? undefined : value,
        });
        toast.success("Lead assignment updated");
      } catch (error) {
        toast.error(error instanceof Error ? error.message : "Failed to assign lead");
      }
    },
    [assignLead, authUserId, lead],
  );

  const handleLogActivity = React.useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!lead) return;
      setLogging(true);
      try {
        await createActivity({
          authUserId,
          leadId: lead._id,
          type: activityType,
          outcome: activityOutcome,
          note: activityNote,
          nextFollowUpAt: activityFollowUpAt
            ? dateInputStringToLocalStartOfDayMs(activityFollowUpAt)
            : undefined,
          statusAfter: activityStatus === NO_STATUS_CHANGE_VALUE ? undefined : activityStatus,
        });
        toast.success("Activity logged");
        setActivityNote("");
        setActivityFollowUpAt("");
        setActivityStatus(NO_STATUS_CHANGE_VALUE);
      } catch (error) {
        toast.error(error instanceof Error ? error.message : "Failed to log activity");
      } finally {
        setLogging(false);
      }
    },
    [activityFollowUpAt, activityNote, activityOutcome, activityStatus, activityType, authUserId, createActivity, lead],
  );

  return (
    <div className="space-y-3 [&_[data-slot=card]]:bg-background [&_[data-slot=card]]:shadow-none [&_[data-slot=input]]:bg-background [&_[data-slot=input]]:shadow-none [&_[data-slot=select-trigger]]:bg-background [&_[data-slot=select-trigger]]:shadow-none [&_[data-slot=textarea]]:bg-background [&_[data-slot=textarea]]:shadow-none">
      <section className="overflow-hidden rounded-2xl border bg-background">
        <div className="border-b bg-background px-5 py-5">
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div className="min-w-0">
              <h2 className="truncate text-2xl font-semibold tracking-tight [text-wrap:balance]">{lead.companyName}</h2>
              <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground [text-wrap:pretty]">
                {formatLocation(lead) ? (
                  <span className="inline-flex items-center gap-1">
                    <MapPin className="h-3.5 w-3.5" />
                    {formatLocation(lead)}
                  </span>
                ) : null}
                {lead.phone ? (
                  <span className="inline-flex items-center gap-1">
                    <Phone className="h-3.5 w-3.5" />
                    {lead.phone}
                  </span>
                ) : null}
                {lead.nextFollowUpAt ? <span>Follow up {formatOptionalDate(lead.nextFollowUpAt)}</span> : null}
              </p>
            </div>
            <div className="flex shrink-0 flex-wrap gap-2">
              <Badge variant="outline" className={STATUS_TONES[lead.status]}>{getOptionLabel(STATUS_OPTIONS, lead.status)}</Badge>
              <Badge variant="outline" className={PRIORITY_TONES[lead.priority]}>{getOptionLabel(PRIORITY_OPTIONS, lead.priority)} priority</Badge>
            </div>
          </div>
        </div>
        <div className="space-y-4 p-5">
          {canAssign ? (
            <div className="grid gap-2 rounded-xl border bg-background p-3 md:grid-cols-[132px_1fr] md:items-center">
              <Label className="text-xs font-medium text-muted-foreground">Owner</Label>
              <Select value={lead.assignedToUserProfileId ?? UNASSIGNED_VALUE} onValueChange={(value) => void handleAssign(value)}>
                <SelectTrigger className="h-9 w-full bg-background shadow-none">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={UNASSIGNED_VALUE}>Unassigned</SelectItem>
                  {assignees.map((assignee) => (
                    <SelectItem key={assignee._id} value={assignee._id}>
                      {assignee.name ?? assignee.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}
          <form onSubmit={(event) => void handleSave(event)} className="space-y-4">
            <div className="rounded-xl border bg-background p-4">
              <div className="mb-3 flex items-center justify-between gap-3">
                <div>
                  <h3 className="text-sm font-medium">Lead details</h3>
                  <p className="text-xs text-muted-foreground">Keep only the fields sales needs for the next touch.</p>
                </div>
                <Button type="submit" size="sm" disabled={saving || !hasUnsavedChanges} className={cn(hasUnsavedChanges && "bg-amber-600 text-white hover:bg-amber-700")}>{saving ? "Saving..." : hasUnsavedChanges ? "Save changes" : "Saved"}</Button>
              </div>
              <LeadFormFields draft={draft} setDraft={setDraft} assignees={assignees} showAssignee={false} />
              {hasUnsavedChanges ? (
                <div className="sticky bottom-3 z-10 mt-5 flex items-center justify-between gap-3 rounded-xl border bg-background/95 p-3 backdrop-blur">
                  <p className="text-sm font-medium">Unsaved lead changes</p>
                  <Button type="submit" disabled={saving} className="bg-amber-600 text-white hover:bg-amber-700">{saving ? "Saving..." : "Save changes"}</Button>
                </div>
              ) : null}
            </div>
          </form>
        </div>
      </section>

      <section className="rounded-2xl border bg-background p-4">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div className="flex min-w-0 gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border bg-background">
              <MessageSquarePlus className="h-4 w-4 text-muted-foreground" />
            </div>
            <div>
              <h3 className="text-base font-semibold tracking-tight">Add activity</h3>
              <p className="text-sm text-muted-foreground">Log the touch, outcome, and next step in one pass.</p>
            </div>
          </div>
          <Button type="submit" form="lead-activity-form" size="sm" disabled={logging}>{logging ? "Logging..." : "Log"}</Button>
        </div>
        <form id="lead-activity-form" onSubmit={(event) => void handleLogActivity(event)} className="space-y-4">
          <div className="rounded-xl border bg-background p-3">
            <div className="grid gap-3 md:grid-cols-4">
              <SelectField label="Touch" value={activityType} options={ACTIVITY_TYPE_OPTIONS} onValueChange={setActivityType} />
              <SelectField label="Outcome" value={activityOutcome} options={OUTCOME_OPTIONS} onValueChange={setActivityOutcome} />
              <div className="space-y-2">
                <Label>Status after</Label>
                <Select value={activityStatus} onValueChange={setActivityStatus}>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_STATUS_CHANGE_VALUE}>No status change</SelectItem>
                    {STATUS_OPTIONS.map((status) => (
                      <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="activity-follow-up">Next follow-up</Label>
                <DatePickerField id="activity-follow-up" value={activityFollowUpAt} onChange={setActivityFollowUpAt} />
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="activity-note">Conversation notes</Label>
            <Textarea
              id="activity-note"
              value={activityNote}
              onChange={(event) => setActivityNote(event.target.value)}
              className="min-h-28 shadow-none"
              placeholder="Example: reached Brian, currently buying from local importer, open to samples next week..."
            />
          </div>
        </form>
      </section>

      <section className="rounded-2xl border bg-background p-4">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold tracking-tight">Activity timeline</h3>
            <p className="text-sm text-muted-foreground">
              {activities === undefined ? "Loading activity..." : `${activities.length} CRM touch${activities.length === 1 ? "" : "es"} logged`}
            </p>
          </div>
          <Badge variant="outline" className="border-border/80 bg-background text-muted-foreground">
            Tracking
          </Badge>
        </div>
        {activities?.length ? (
          <ol className="relative space-y-0 pl-9 before:absolute before:bottom-3 before:left-[17px] before:top-3 before:w-px before:bg-border">
            {activities.map((activity, index) => (
              <li key={activity._id} className="relative pb-5 last:pb-0">
                <div className="absolute -left-9 top-0 flex h-9 w-9 items-center justify-center rounded-full border bg-background">
                  {activity.nextFollowUpAt ? <CalendarClock className="h-4 w-4 text-muted-foreground" /> : <MessageSquarePlus className="h-4 w-4 text-muted-foreground" />}
                </div>
                <div className="rounded-xl border bg-background p-3">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant="outline">{getOptionLabel(ACTIVITY_TYPE_OPTIONS, activity.type)}</Badge>
                        {activity.outcome ? <Badge variant="outline" className={BADGE_TONES.sky}>{getOptionLabel(OUTCOME_OPTIONS, activity.outcome)}</Badge> : null}
                        {activity.statusAfter ? <Badge variant="outline" className={STATUS_TONES[activity.statusAfter]}>{getOptionLabel(STATUS_OPTIONS, activity.statusAfter)}</Badge> : null}
                      </div>
                      <p className="text-xs text-muted-foreground">Step {activities.length - index} · {formatRelativeTime(activity.createdAt)} by {activity.createdByName ?? "—"}</p>
                    </div>
                    {activity.nextFollowUpAt ? (
                      <Badge variant="outline" className="border-border/80 bg-background text-muted-foreground">Follow up {formatOptionalDate(activity.nextFollowUpAt)}</Badge>
                    ) : null}
                  </div>
                  <p className="mt-3 whitespace-pre-wrap text-sm leading-6">{activity.note}</p>
                </div>
              </li>
            ))}
          </ol>
        ) : (
          <div className="rounded-xl border bg-background p-4 text-sm text-muted-foreground">
            No activity logged yet. Log the first touch above to start the timeline.
          </div>
        )}
      </section>
    </div>
  );
}

function LeadDetailDrawer({
  lead,
  activities,
  assignees,
  canAssign,
  authUserId,
  onClose,
}: {
  lead: CrmLead | null;
  activities: CrmActivity[] | undefined;
  assignees: SalesAssignee[];
  canAssign: boolean;
  authUserId?: string;
  onClose: () => void;
}) {
  return (
    <Sheet open={Boolean(lead)} onOpenChange={(open) => {
      if (!open) onClose();
    }}>
      <SheetContent
        side="right"
        onOpenAutoFocus={(event) => event.preventDefault()}
        className="h-dvh max-h-dvh w-full overflow-y-auto !border-0 !border-l-0 bg-sidebar p-4 shadow-none outline-none ring-0 duration-200 sm:max-w-2xl"
      >
        <SheetHeader className="sr-only">
          <SheetTitle>{lead ? lead.companyName : "Lead details"}</SheetTitle>
          <SheetDescription>Review and update lead details, activities, and follow-up notes.</SheetDescription>
        </SheetHeader>
        <LeadDetail
          lead={lead}
          activities={activities}
          assignees={assignees}
          canAssign={canAssign}
          authUserId={authUserId}
        />
      </SheetContent>
    </Sheet>
  );
}

function CrmViewTabs({
  view,
  onViewChange,
}: {
  view: CrmWorkspaceView;
  onViewChange: (view: CrmWorkspaceView) => void;
}) {
  const options: Array<{ value: CrmWorkspaceView; label: string; icon: React.ComponentType<{ className?: string }> }> = [
    { value: "pipeline", label: "Pipeline", icon: Columns3 },
    { value: "followups", label: "Follow-ups", icon: ListChecks },
    { value: "table", label: "Table", icon: ClipboardList },
  ];

  return (
    <div className="inline-flex w-fit max-w-full items-center gap-1 rounded-lg border bg-background p-1">
      {options.map((option) => {
        const Icon = option.icon;
        const isActive = view === option.value;
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onViewChange(option.value)}
            className={cn(
              "flex h-8 items-center gap-1.5 rounded-md px-2.5 text-sm transition-colors",
              isActive ? "bg-muted text-foreground" : "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
            )}
          >
            <Icon className="h-3.5 w-3.5 shrink-0" />
            <span>{option.label}</span>
          </button>
        );
      })}
    </div>
  );
}

function LeadCardContent({ lead }: { lead: CrmLead }) {
  const isOverdue = Boolean(lead.nextFollowUpAt && lead.nextFollowUpAt < getStartOfTodayMs());
  return (
    <div className="space-y-2">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-foreground">{lead.companyName}</p>
          <p className="truncate text-xs text-muted-foreground">{formatLocation(lead) || lead.website || lead.source || "No location"}</p>
        </div>
        <Badge variant="outline" className={cn(PRIORITY_TONES[lead.priority], "shrink-0")}>{getOptionLabel(PRIORITY_OPTIONS, lead.priority)}</Badge>
      </div>
      <div className="flex flex-wrap gap-1.5">
        <Badge variant="outline" className={STATUS_TONES[lead.status]}>{getOptionLabel(STATUS_OPTIONS, lead.status)}</Badge>
        <Badge variant="outline" className={BADGE_TONES.slate}>{getOptionLabel(APPROACH_OPTIONS, lead.approach)}</Badge>
      </div>
      <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
        <span className="truncate">{lead.assignedToName ?? "Unassigned"}</span>
        <span className={cn("shrink-0 tabular-nums", isOverdue && "font-medium text-red-600")}>{lead.nextFollowUpAt ? formatOptionalDate(lead.nextFollowUpAt) : "No follow-up"}</span>
      </div>
    </div>
  );
}

function PipelineLeadCard({
  lead,
  onOpenLead,
}: {
  lead: CrmLead;
  onOpenLead: (leadId: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: lead._id });
  const style = { transform: CSS.Translate.toString(transform) };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "w-full rounded-lg border border-border/70 bg-background p-2.5 transition-[border-color,opacity] hover:border-border",
        isDragging && "relative z-10 opacity-80",
      )}
    >
      <div className="flex gap-2">
        <button
          type="button"
          aria-label={`Move ${lead.companyName}`}
          className="mt-0.5 flex h-8 w-6 shrink-0 cursor-grab items-start justify-center rounded-md text-muted-foreground active:cursor-grabbing"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" />
        </button>
        <button type="button" className="min-w-0 flex-1 text-left active:scale-[0.99]" onClick={() => onOpenLead(lead._id)}>
          <LeadCardContent lead={lead} />
        </button>
      </div>
    </div>
  );
}

function PipelineColumn({
  status,
  leads,
  onOpenLead,
}: {
  status: LeadStatus;
  leads: CrmLead[];
  onOpenLead: (leadId: string) => void;
}) {
  const { isOver, setNodeRef } = useDroppable({ id: status });

  return (
    <section ref={setNodeRef} className={cn("flex min-h-[420px] w-[248px] shrink-0 flex-col rounded-xl border border-border/70 bg-background p-2 transition-colors", isOver && "border-primary bg-primary/5")}>
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={STATUS_TONES[status]}>{getOptionLabel(STATUS_OPTIONS, status)}</Badge>
          <span className="text-xs tabular-nums text-muted-foreground">{leads.length}</span>
        </div>
      </div>
      <div className="space-y-2">
        {leads.map((lead) => <PipelineLeadCard key={lead._id} lead={lead} onOpenLead={onOpenLead} />)}
        {leads.length === 0 ? (
          <div className="rounded-xl border border-dashed bg-background/60 p-4 text-center text-xs text-muted-foreground">Drop leads here</div>
        ) : null}
      </div>
    </section>
  );
}

function LeadPipelineBoardSkeleton() {
  return (
    <Card className="rounded-xl border-border/70">
      <CardHeader className="px-4 py-3">
        <Skeleton className="h-5 w-32" />
        <Skeleton className="h-4 w-80 max-w-full" />
      </CardHeader>
      <CardContent className="px-4 pb-4">
        <div className="flex gap-3 overflow-x-auto pb-2">
          {Array.from({ length: 5 }).map((_, columnIndex) => (
            <section
              key={columnIndex}
              className="flex min-h-[420px] w-[248px] shrink-0 flex-col rounded-xl border border-border/70 bg-background p-2"
            >
              <div className="mb-3 flex items-center gap-2">
                <Skeleton className="h-6 w-24 rounded-full" />
                <Skeleton className="h-4 w-5" />
              </div>
              <div className="space-y-2">
                {Array.from({ length: columnIndex % 2 === 0 ? 2 : 1 }).map((_, cardIndex) => (
                  <div key={cardIndex} className="rounded-lg border border-border/70 bg-background p-2.5">
                    <Skeleton className="h-4 w-36" />
                    <Skeleton className="mt-2 h-3 w-28" />
                    <div className="mt-3 flex gap-1.5">
                      <Skeleton className="h-5 w-16 rounded-full" />
                      <Skeleton className="h-5 w-14 rounded-full" />
                    </div>
                    <Skeleton className="mt-3 h-3 w-full" />
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function LeadPipelineBoard({
  leads,
  authUserId,
  onOpenLead,
}: {
  leads: CrmLead[];
  authUserId?: string;
  onOpenLead: (leadId: string) => void;
}) {
  const createActivity = useMutation(api.crm.createLeadActivity);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));
  const leadsByStatus = React.useMemo(() => {
    const groups = new Map<LeadStatus, CrmLead[]>(PIPELINE_STATUSES.map((status) => [status, []]));
    for (const lead of sortLeadsByFollowUp(leads)) groups.get(lead.status)?.push(lead);
    return groups;
  }, [leads]);

  const handleDragEnd = React.useCallback(async (event: DragEndEvent) => {
    const nextStatus = event.over?.id as LeadStatus | undefined;
    if (!nextStatus || !PIPELINE_STATUSES.includes(nextStatus)) return;

    const leadId = String(event.active.id);
    const lead = leads.find((candidate) => candidate._id === leadId);
    if (!lead || lead.status === nextStatus) return;
    if (!authUserId) {
      toast.error("Sign in again before moving leads");
      return;
    }

    try {
      await createActivity({
        authUserId,
        leadId: lead._id,
        type: "status_change",
        note: `Moved from ${getOptionLabel(STATUS_OPTIONS, lead.status)} to ${getOptionLabel(STATUS_OPTIONS, nextStatus)}`,
        statusAfter: nextStatus,
      });
      toast.success(`Moved ${lead.companyName} to ${getOptionLabel(STATUS_OPTIONS, nextStatus)}`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to move lead");
    }
  }, [authUserId, createActivity, leads]);

  return (
    <Card className="rounded-xl border-border/70">
      <CardHeader className="px-4 py-3">
        <CardTitle className="text-base">Sales pipeline</CardTitle>
        <CardDescription>Drag roasters through stages, then open a card for notes and follow-up.</CardDescription>
      </CardHeader>
      <CardContent className="px-4 pb-4">
        <DndContext sensors={sensors} onDragEnd={(event) => void handleDragEnd(event)}>
          <div className="flex gap-3 overflow-x-auto pb-2">
            {PIPELINE_STATUSES.map((status) => (
              <PipelineColumn key={status} status={status} leads={leadsByStatus.get(status) ?? []} onOpenLead={onOpenLead} />
            ))}
          </div>
        </DndContext>
      </CardContent>
    </Card>
  );
}

function LeadFollowUpsView({
  leads,
  onOpenLead,
}: {
  leads: CrmLead[];
  onOpenLead: (leadId: string) => void;
}) {
  const buckets: Array<{ id: FollowUpBucket; title: string; description: string }> = [
    { id: "overdue", title: "Overdue", description: "Needs recovery today" },
    { id: "thisWeek", title: "Next 7 days", description: "Scheduled touches" },
    { id: "later", title: "Later", description: "Future follow-up" },
    { id: "noFollowUp", title: "No next step", description: "Needs a date" },
  ];
  const leadsByBucket = React.useMemo(() => {
    const groups = new Map<FollowUpBucket, CrmLead[]>(buckets.map((bucket) => [bucket.id, []]));
    for (const lead of sortLeadsByFollowUp(leads)) groups.get(getFollowUpBucket(lead))?.push(lead);
    return groups;
  }, [buckets, leads]);

  return (
    <div className="grid gap-4 xl:grid-cols-4">
      {buckets.map((bucket) => {
        const bucketLeads = leadsByBucket.get(bucket.id) ?? [];
        return (
          <Card key={bucket.id} className="rounded-xl border-border/70">
            <CardHeader className="px-4 py-3">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <CardTitle className="text-base">{bucket.title}</CardTitle>
                  <CardDescription>{bucket.description}</CardDescription>
                </div>
                <Badge variant="outline" className="tabular-nums">{bucketLeads.length}</Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-2 px-4 pb-4">
              {bucketLeads.slice(0, 12).map((lead) => (
                <button key={lead._id} type="button" onClick={() => onOpenLead(lead._id)} className="w-full rounded-lg border border-border/70 bg-background p-2.5 text-left transition-[border-color,scale] hover:border-border active:scale-[0.98]">
                  <LeadCardContent lead={lead} />
                </button>
              ))}
              {bucketLeads.length === 0 ? <p className="rounded-xl bg-muted/40 p-4 text-sm text-muted-foreground">Nothing here.</p> : null}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

function LeadFollowUpsSkeleton() {
  return (
    <div className="grid gap-4 xl:grid-cols-4">
      {["Overdue", "Next 7 days", "Later", "No next step"].map((title, index) => (
        <Card key={title} className="rounded-xl border-border/70">
          <CardHeader className="px-4 py-3">
            <div className="flex items-center justify-between gap-2">
              <div className="space-y-2">
                <Skeleton className="h-5 w-24" />
                <Skeleton className="h-3 w-32" />
              </div>
              <Skeleton className="h-5 w-7 rounded-full" />
            </div>
          </CardHeader>
          <CardContent className="space-y-2 px-4 pb-4">
            {Array.from({ length: index === 0 ? 2 : 1 }).map((_, rowIndex) => (
              <div key={rowIndex} className="rounded-lg border border-border/70 bg-background p-2.5">
                <Skeleton className="h-4 w-36" />
                <Skeleton className="mt-2 h-3 w-24" />
                <Skeleton className="mt-3 h-5 w-28 rounded-full" />
              </div>
            ))}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

const LeadQueueCard = React.memo(function LeadQueueCard({
  leads,
  leadRows,
  leadColumns,
  showsAllLeads,
}: {
  leads: CrmLead[] | undefined;
  leadRows: CrmLead[];
  leadColumns: ColumnDef<CrmLead>[];
  showsAllLeads: boolean;
}) {
  return (
    <Card className="rounded-xl border-border/70">
      <CardHeader className="px-4 py-3">
        <CardTitle className="text-base">Lead queue</CardTitle>
        <CardDescription>
          {leads === undefined
            ? "Loading leads..."
            : showsAllLeads
              ? `${leadRows.length} leads. Use the action button to open notes; edit status, owner, priority, approach, and fit inline.`
              : `${leadRows.length} assigned leads. Use the action button to open notes; update status, priority, approach, and fit inline.`}
        </CardDescription>
      </CardHeader>
      <CardContent className="px-4 pb-4">
        <div className="[&_table]:border-separate [&_table]:border-spacing-0 [&_tbody_tr:last-child_td]:border-b [&_td:first-child]:w-[72px] [&_td:first-child]:px-2 [&_td]:border-b [&_td]:border-r [&_td]:border-border/60 [&_td]:px-2.5 [&_td]:py-2 [&_th:first-child]:w-[72px] [&_th:first-child]:px-2 [&_th]:border-b [&_th]:border-r [&_th]:border-border/60 [&_th]:bg-muted/20 [&_th]:px-2.5 [&_th]:text-xs [&_th]:font-medium [&_th]:text-muted-foreground [&_tr:hover_td]:bg-muted/25">
          {leads === undefined ? (
            <DataTableSkeleton columnCount={leadColumns.length} rowCount={8} withPagination />
          ) : (
            <InternalEntityDataTable
              data={leadRows}
              columns={leadColumns}
              pageSize={25}
              enableSelection={false}
              initialSorting={[{ id: "nextFollowUpAt", desc: false }]}
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
});

type LeadsWorkspaceMode = "admin" | "sales";

export function LeadsWorkspace({ mode }: { mode: LeadsWorkspaceMode }) {
  const { authUserId, userRole } = useAuthIdentity();
  const { canUseCrm, canAssign, showsAllLeads } = getCrmWorkspaceAccess({ userRole });
  const [selectedLeadId, setSelectedLeadId] = React.useState<string | null>(null);
  const [createOpen, setCreateOpen] = React.useState(false);
  const [importOpen, setImportOpen] = React.useState(false);
  const [workspaceView, setWorkspaceView] = React.useState<CrmWorkspaceView>(() => {
    if (typeof window !== "undefined") {
      const saved = window.localStorage.getItem(`${CRM_VIEW_STORAGE_KEY}.${mode}`);
      if (saved === "pipeline" || saved === "followups" || saved === "table") return saved;
    }
    return mode === "sales" ? "pipeline" : "table";
  });

  React.useEffect(() => {
    window.localStorage.setItem(`${CRM_VIEW_STORAGE_KEY}.${mode}`, workspaceView);
  }, [mode, workspaceView]);

  const leads = useQuery(api.crm.listLeads, authUserId && canUseCrm ? { authUserId } : "skip") as CrmLead[] | undefined;
  const assignees = useQuery(api.crm.listSalesAssignees, authUserId && canUseCrm ? { authUserId } : "skip") as SalesAssignee[] | undefined;
  const selectedLead = React.useMemo(
    () => selectedLeadId ? (leads?.find((lead) => lead._id === selectedLeadId) ?? null) : null,
    [leads, selectedLeadId],
  );
  const activities = useQuery(
    api.crm.listLeadActivities,
    authUserId && selectedLead ? { authUserId, leadId: selectedLead._id } : "skip",
  ) as CrmActivity[] | undefined;

  const leadRows = React.useMemo(() => leads ?? [], [leads]);
  const assigneeRows = React.useMemo(() => assignees ?? [], [assignees]);
  const handleOpenLead = React.useCallback((leadId: string) => setSelectedLeadId(leadId), []);
  const handleCloseLead = React.useCallback(() => setSelectedLeadId(null), []);
  const leadColumns = useLeadTableColumns({
    data: leadRows,
    assignees: assigneeRows,
    authUserId,
    canAssign,
    onOpenLead: handleOpenLead,
  });

  const stats = React.useMemo(() => {
    const all = leads ?? [];
    return {
      total: all.length,
      needsContact: all.filter((lead) => lead.status === "new" || lead.status === "assigned").length,
      followUps: all.filter((lead) => lead.nextFollowUpAt && lead.nextFollowUpAt <= Date.now() + 7 * 24 * 60 * 60 * 1000).length,
      converted: all.filter((lead) => lead.status === "converted").length,
    };
  }, [leads]);
  const isInitialLoading = leads === undefined;

  if (!canUseCrm) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>{showsAllLeads ? "Lead Management" : "My Leads"}</CardTitle>
            <CardDescription>You do not have access to this leads workspace.</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight [text-wrap:balance]">
            {showsAllLeads ? "Lead Management" : "My Leads"}
          </h1>
          <p className="mt-1 max-w-3xl text-muted-foreground [text-wrap:pretty]">
            {showsAllLeads
              ? "Upload roaster lead lists, assign owners, and keep sales outreach organized."
              : "Work your assigned roaster leads, log calls, and keep follow-ups current."}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canAssign ? (
            <Button variant="outline" onClick={() => setImportOpen(true)} className="active:scale-[0.96] transition-transform">
              <FileUp className="mr-2 h-4 w-4" />
              Import CSV
            </Button>
          ) : null}
          {canAssign ? (
            <Button onClick={() => setCreateOpen(true)} className="active:scale-[0.96] transition-transform">
              <UserPlus className="mr-2 h-4 w-4" />
              Add lead
            </Button>
          ) : null}
        </div>
      </div>

      <div className="space-y-4">
        {isInitialLoading ? (
          <MetricGridSkeleton />
        ) : (
          <div className="grid gap-3 md:grid-cols-4">
            <StatCard title="Visible leads" value={stats.total} description={showsAllLeads ? "All imported prospects." : "Assigned to you."} icon={ClipboardList} />
            <StatCard title="Needs first touch" value={stats.needsContact} description="New or assigned leads." icon={Phone} />
            <StatCard title="Follow-ups" value={stats.followUps} description="Due in the next 7 days." icon={CalendarClock} />
            <StatCard title="Converted" value={stats.converted} description="Marked account-created." icon={CheckCircle2} />
          </div>
        )}

        <CrmViewTabs view={workspaceView} onViewChange={setWorkspaceView} />

        {isInitialLoading && workspaceView === "pipeline" ? (
          <LeadPipelineBoardSkeleton />
        ) : isInitialLoading && workspaceView === "followups" ? (
          <LeadFollowUpsSkeleton />
        ) : workspaceView === "pipeline" ? (
          <LeadPipelineBoard leads={leadRows} authUserId={authUserId} onOpenLead={handleOpenLead} />
        ) : workspaceView === "followups" ? (
          <LeadFollowUpsView leads={leadRows} onOpenLead={handleOpenLead} />
        ) : (
          <LeadQueueCard
            leads={leads}
            leadRows={leadRows}
            leadColumns={leadColumns}
            showsAllLeads={showsAllLeads}
          />
        )}
      </div>

      <LeadDetailDrawer
        lead={selectedLead}
        activities={activities}
        assignees={assigneeRows}
        canAssign={canAssign}
        authUserId={authUserId}
        onClose={handleCloseLead}
      />

      <CreateLeadDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        assignees={assigneeRows}
        canAssign={canAssign}
        authUserId={authUserId}
      />
      <ImportLeadsDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        assignees={assigneeRows}
        authUserId={authUserId}
      />
    </div>
  );
}

export default function SalesCrmPage() {
  return <LeadsWorkspace mode="sales" />;
}
